import React, { useMemo } from "react";

import { useQuery } from "@apollo/client";
import { Grid, Typography, Link, CircularProgress, Box, Alert } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { USERS_WATERINGS } from "api/queries";
import { UserWatering } from "api/types";

type ReportsContentProps = {
  queryDates: { start: string; end: string } | null;
};

const ReportsContent = ({ queryDates }: ReportsContentProps) => {
  const { data, loading, error } = useQuery<{ getWaterings: UserWatering[] }>(USERS_WATERINGS, {
    variables: {
      dateStart: queryDates?.start || "",
      dateEnd: queryDates?.end || "",
    },
    skip: !queryDates,
  });

  const waterings = useMemo(() => {
    if (!data?.getWaterings) return [];
    return data.getWaterings;
  }, [data]);

  const userWaterings = useMemo(() => {
    // Sort waterings by count desc, then name asc
    return [...waterings].sort((a, b) => {
      if (a.waterings !== b.waterings) {
        return b.waterings - a.waterings;
      }
      return a.user.name.localeCompare(b.user.name);
    });
  }, [waterings]);

  if (!queryDates) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        Please select a date range and submit to see the data.
      </Alert>
    );
  }

  if (loading)
    return (
      <Box display={"flex"} justifyContent={"center"} mt={2}>
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error: {error.message}
      </Alert>
    );
  if (userWaterings.length === 0)
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        No data available for the selected date range.
      </Alert>
    );

  return (
    <div>
      <Typography variant="h6" gutterBottom>
        Users and Waterings
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Typography variant="subtitle1">User Name</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1">Total Waterings</Typography>
        </Grid>
      </Grid>
      {userWaterings.map(({ user, waterings }) => (
        <Grid key={user.id} container spacing={3}>
          <Grid item xs={6}>
            <Link component={RouterLink} to={`/reports/${user.id}?start=${queryDates.start}&end=${queryDates.end}`}>
              {user.name}
            </Link>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1">{waterings}</Typography>
          </Grid>
        </Grid>
      ))}
    </div>
  );
};

export { ReportsContent };
